import type {
  FairValueHistogramBucket,
  FairValueHistogramCompanyFragment,
} from '../fairValueHistogramFragment';
import { scaleBand, scaleLinear } from '@visx/scale';
import useResizeObserver from 'use-resize-observer';
import { FairValueHistogramChartBars } from './FairValueHistogramChartBars';
import { MARGIN_BOTTOM } from '../utils';
import { FairValueHistogramChartXAxis } from './FairValueHistogramChartXAxis';
import { FairValueHistogramChartMarkers } from './FairValueHistogramChartMarkers';
import { useMemo, useRef, useState } from 'react';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { FairValueHistogramTooltip } from './FairValueHistogramTooltip';
import { FairValueHistogramChartInteractiveArea } from './FairValueHistogramChartInteractiveArea';

type Props = {
  company: FairValueHistogramCompanyFragment;
  height: number;
  width?: number;
};

export const FairValueHistogramChart = ({
  company,
  height = 160,
  width: staticWidth, // The static width is used for solocharts - SSR with headless chrome
}: Props) => {
  const { buckets, min, max } = company.valuationHistogram;
  const currencyIso =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedBucket, setSelectedBucket] =
    useState<FairValueHistogramBucket | null>(null);
  const { width: chartWidth } = useResizeObserver({
    ref: containerRef,
  });
  const width = staticWidth ?? chartWidth ?? 0;

  const xMax = width;
  const yMax = height - MARGIN_BOTTOM;

  const { xBandScale, xScale, yScale } = useMemo(() => {
    const xBandScale = scaleBand({
      domain: buckets.map((b) => b.min),
      range: [0, xMax],
      paddingInner: 0.2,
      paddingOuter: 0.2,
    });
    const xScale = scaleLinear({
      domain: [min ?? 0, max ?? 0],
      range: [0, xMax],
      round: true,
      clamp: true,
    });

    const yScale = scaleLinear({
      domain: [0, Math.max(...buckets.map((b) => b.count))],
      range: [yMax, 0],
      clamp: true,
    });
    return { xBandScale, xScale, yScale };
  }, [yMax, buckets, min, max, xMax]);

  return (
    <>
      <FairValueHistogramTooltip
        selectedBucket={selectedBucket}
        xBandScale={xBandScale}
        containerElement={containerRef.current}
        currencyIso={currencyIso}
        fairValue={company.myValuation?.fairValue}
        sharePrice={company.analysisValue?.lastSharePrice}
      />
      <div
        ref={containerRef}
        style={{ height }}
        id="fair-value-histogram-chart"
      >
        <svg width="100%" height="100%">
          {!selectedBucket && <title>Fair Value Histogram</title>}
          <FairValueHistogramChartMarkers
            fairValue={company.myValuation?.fairValue}
            lastSharePrice={company.analysisValue?.lastSharePrice}
            xScale={xScale}
            y={yMax}
            currencyIso={currencyIso}
          />
          <FairValueHistogramChartXAxis
            yMax={yMax + 0.5}
            buckets={buckets}
            xBandScale={xBandScale}
            currencyIso={currencyIso}
          />
          <FairValueHistogramChartBars
            xBandScale={xBandScale}
            yScale={yScale}
            buckets={buckets}
            height={yMax}
            selectedBucket={selectedBucket}
          />
          <FairValueHistogramChartInteractiveArea
            xBandScale={xBandScale}
            buckets={buckets}
            onBucketSelected={setSelectedBucket}
          />
        </svg>
      </div>
    </>
  );
};
